import objectPath from 'object-path';
import lscache from 'lscache';
import { useMemo, useState } from 'react';
import { Nullable } from 'src/types/nullable.type';
import { apiCheckIfNdaSigned } from 'src/utils/journeyApi';

export const useEmbedJourneyFlags = (
  journey: any,
  uuid: string,
  userEmail: Nullable<string>,
  passwordIsVerified: boolean,
  galleryMode: boolean,
) => {
  const [isNdaSignatureRequired, setIsNdaSignatureRequired] = useState(false);
  const [isEmailGateUnlocked, setIsEmailGateUnlocked] = useState<boolean>(false);

  const isJourneyPreviouslyAccessed = useMemo(() => {
    let storedEmail: string = lscache.get(`${uuid}-email`);
    if (!storedEmail || typeof storedEmail !== 'string') {
      storedEmail = '';
    }

    if (storedEmail.toLowerCase() === userEmail?.toLowerCase()) {
      // this parameter has been included as there is no better way to trigger this hook after submit
      return true;
    } else {
      return false;
    }
  }, [uuid, userEmail]);

  const isJourneyInactive = useMemo(
    () => objectPath.get(journey, 'is_expired', false) || !objectPath.get(journey, 'enabled'),
    [journey]
  );

  const isJourneyPasswordProtected = useMemo(
    () =>
      !isEmailGateUnlocked &&
      !galleryMode &&
      !isJourneyPreviouslyAccessed &&
      objectPath.get(journey, 'has_password', false) &&
      !passwordIsVerified,
    [journey, galleryMode, isJourneyPreviouslyAccessed, passwordIsVerified, isEmailGateUnlocked]
  );

  const isJourneyEmailGated = useMemo(
    () =>
      !isEmailGateUnlocked &&
      !galleryMode &&
      !isJourneyPreviouslyAccessed &&
      (objectPath.get(journey, 'data.is_email_required', false) || objectPath.get(journey, 'email_required', false)),
    [journey, galleryMode, isJourneyPreviouslyAccessed, isEmailGateUnlocked]
  );

  const isEmailVerificationRequired = useMemo(
    () => objectPath.get(journey, 'email_verification', false) && isJourneyEmailGated,
    [journey, isJourneyEmailGated]
  );

  const isNdaRequired = useMemo(
    () => objectPath.get(journey, 'nda_required', false),
    [journey]
  );

  const checkIfNdaSignatureRequired = (email: Nullable<string>) => {
    if (isNdaRequired && email) {
      // check if this email signed the nda before
      return apiCheckIfNdaSigned(journey.alias.uuid, email).then((signed) => {
        setIsNdaSignatureRequired(false);
        return Promise.resolve(false);
      }).catch(() => {
        setIsNdaSignatureRequired(true);
        return Promise.resolve(true);
      });
    } else {
      setIsNdaSignatureRequired(false);
      return Promise.resolve(false);
    }
  };

  const onNdaSigned = () => {
    setIsNdaSignatureRequired(false);
  };

  const isJourneyWhitelistEmailGated = useMemo(() => {
    const allowedEmails: string[] = objectPath.get(journey, 'allowed_emails', []);
    const blacklistedEmails: string[] = objectPath.get(journey, 'blacklisted_emails', []);

    if (allowedEmails.length > 0 || blacklistedEmails.length > 0) {
      return !userEmail || !allowedEmails.includes(userEmail) || blacklistedEmails.includes(userEmail);
    } else {
      return false;
    }
  }, [journey, userEmail]);

  const unlockEmailGate = () => {
    setIsEmailGateUnlocked(true);
  }

  return {
    isJourneyInactive,
    isJourneyPasswordProtected,
    isJourneyEmailGated,
    isEmailVerificationRequired,
    isNdaRequired,
    isNdaSignatureRequired,
    isEmailGateUnlocked,
    checkIfNdaSignatureRequired,
    onNdaSigned,
    unlockEmailGate,
    isJourneyWhitelistEmailGated,
  };
};
